
import { useEffect, useMemo } from 'react';
import type { BookingWindow } from '@shared/booking';
import {
  startOfDay,
  addWeeks,
  addDays as addDaysInstant,
  viewerDayKey,
} from '../week-grid.ts';
import { useCalendarAxis, type CalendarAxisRange } from '@client/shared/hooks/use-calendar-axis.ts';
import { SLOT_MINUTES } from '@shared/booking';
import { formatSlotLabel } from '@client/shared/lib/wall-clock-intake.ts';
import type { SlotGridColumn, SlotGridRow } from './SlotGrid.tsx';

export type SlotCalendarRange = CalendarAxisRange;

export function rangeDayCount(range: SlotCalendarRange): number {
  return range === 'week' ? 7 : range === '3day' ? 3 : 1;
}

export interface UseSlotCalendarGridArgs {
  readonly zone: string;
  readonly anchor: Date;
  readonly range: SlotCalendarRange;
  readonly windowEnd: Date;
  readonly visibleStartMin: number;
  readonly visibleEndMin: number;
  readonly now?: Date;
}

export interface UseSlotCalendarGrid {
  readonly days: string[];
  readonly columns: SlotGridColumn[];
  readonly rows: SlotGridRow[];
  readonly window: BookingWindow;
  readonly todayKey: string;
  inWindow(dateStr: string): boolean;
  readonly canStepBack: boolean;
  readonly canStepForward: boolean;
}

export function useSlotCalendarGrid({
  zone,
  anchor,
  range,
  windowEnd,
  visibleStartMin,
  visibleEndMin,
  now,
}: UseSlotCalendarGridArgs): UseSlotCalendarGrid {
  const nowInstant = useMemo(() => now ?? new Date(), [now]);
  const window = useMemo<BookingWindow>(
    () => ({ windowStart: nowInstant, windowEnd }),
    [nowInstant, windowEnd],
  );

  const { dayStarts, days, columnHeaders, todayKey } = useCalendarAxis({
    anchor,
    range,
    zone,
    now: nowInstant,
  });

  const columns = useMemo<SlotGridColumn[]>(
    () => columnHeaders.map((c) => ({ date: c.key, label: c.label, isToday: c.isToday })),
    [columnHeaders],
  );

  const rows = useMemo<SlotGridRow[]>(() => {
    const out: SlotGridRow[] = [];
    for (let m = visibleStartMin; m < visibleEndMin; m += SLOT_MINUTES) {
      const slot = m / SLOT_MINUTES;
      out.push({ slot, label: slot % 2 === 0 ? formatSlotLabel(m) : '' });
    }
    return out;
  }, [visibleStartMin, visibleEndMin]);

  const inWindow = useMemo(() => {
    const overlaps = (dayStart: Date) => {
      const next = addDaysInstant(dayStart, zone, 1).getTime();
      return dayStart.getTime() < window.windowEnd.getTime() && next > window.windowStart.getTime();
    };
    return (dateStr: string) => {
      const idx = days.indexOf(dateStr);
      const dayStart = idx >= 0 ? dayStarts[idx]! : startOfDay(dayKeyAnchor(dateStr), zone);
      return overlaps(dayStart);
    };
  }, [days, dayStarts, zone, window]);

  const { canStepBack, canStepForward } = useMemo(() => {
    const periodStart = dayStarts[0]!;
    const span = rangeDayCount(range);
    const shift = (delta: number) =>
      range === 'week'
        ? addWeeks(periodStart, zone, delta)
        : addDaysInstant(periodStart, zone, delta * span);
    const prevLastKey = viewerDayKey(addDaysInstant(shift(-1), zone, span - 1), zone);
    const nextFirstKey = viewerDayKey(shift(1), zone);
    return { canStepBack: inWindow(prevLastKey), canStepForward: inWindow(nextFirstKey) };
  }, [dayStarts, range, zone, inWindow]);

  return { days, columns, rows, window, todayKey, inWindow, canStepBack, canStepForward };
}

export interface UseDayViewWindowSnapArgs {
  readonly range: SlotCalendarRange;
  readonly days: readonly string[];
  inWindow(dateStr: string): boolean;
  onSnap(): void;
}

export function useDayViewWindowSnap({ range, days, inWindow, onSnap }: UseDayViewWindowSnapArgs): void {
  const stranded = range !== 'week' && days.every((d) => !inWindow(d));
  useEffect(() => {
    if (stranded) onSnap();
  }, [stranded, onSnap]);
}

function dayKeyAnchor(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
}
